<template>
  <Card class="mb-4">
    <template #header>
      <div class="flex align-items-center">
        <i class="pi pi-info-circle mr-2"></i>
        <h5 class="m-0">Transaction Details</h5>
      </div>
    </template>
    <template #content>
      <div class="grid">
        <div class="col-12 md:col-6 mb-3">
          <span class="p-float-label">
            <DatePicker
              v-model="localTransactionDate"
              id="transactionDate"
              :showIcon="true"
              dateFormat="yy-mm-dd"
              class="w-full"
            />
            <label for="transactionDate">Transaction Date</label>
          </span>
        </div>

        <div class="col-12 md:col-6 mb-3">
          <span class="p-float-label">
            <Select
              v-model="localTransactionType"
              :options="transactionTypes"
              optionLabel="label"
              optionValue="value"
              placeholder="Select transaction type"
              class="w-full"
            />
            <label for="transactionType">Transaction Type</label>
          </span>
        </div>

        <div class="col-12 md:col-6 mb-3">
          <span class="p-float-label">
            <Select
              v-model="localPaymentMethod"
              :options="paymentMethods"
              optionLabel="label"
              optionValue="value"
              placeholder="Select payment method"
              id="paymentMethod"
              class="w-full"
            />
            <label for="paymentMethod">Payment Method</label>
          </span>
        </div>

        <div class="col-12 md:col-6 mb-3">
          <span class="p-float-label">
            <Select
              v-model="localPaymentStatus"
              :options="paymentStatuses"
              optionLabel="label"
              optionValue="value"
              placeholder="Select payment status"
              id="paymentStatus"
              class="w-full"
            />
            <label for="paymentStatus">Payment Status</label>
          </span>
        </div>

        <div class="col-12">
          <span class="p-float-label">
            <Textarea v-model="localNotes" rows="3" id="notes" class="w-full" />
            <label for="notes">Notes</label>
          </span>
        </div>
      </div>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import Card from 'primevue/card'
import DatePicker from 'primevue/datepicker'
import Select from 'primevue/select'
import Textarea from 'primevue/textarea'

// Define props
const props = defineProps({
  transactionDate: {
    type: Date,
    required: true,
  },
  transactionType: {
    type: String,
    required: true,
  },
  paymentMethod: {
    type: String,
    required: true,
  },
  paymentStatus: {
    type: String,
    required: true,
  },
  notes: {
    type: String,
    default: '',
  },
})

// Define emits
const emit = defineEmits([
  'update:transactionDate',
  'update:transactionType',
  'update:paymentMethod',
  'update:paymentStatus',
  'update:notes',
])

// Local reactive state
const localTransactionDate = ref(props.transactionDate)
const localTransactionType = ref(props.transactionType)
const localPaymentMethod = ref(props.paymentMethod)
const localPaymentStatus = ref(props.paymentStatus)
const localNotes = ref(props.notes)

// Transaction types with icons (restricted to Purchase and Sale)
const transactionTypes = [
  { label: 'Purchase', value: 'purchase', icon: 'pi pi-cart-plus' },
  { label: 'Sale', value: 'sale', icon: 'pi pi-dollar' },
]

// Payment methods
const paymentMethods = [
  { label: 'Cash', value: 'cash' },
  { label: 'Check', value: 'check' },
  { label: 'Bank Transfer', value: 'bank_transfer' },
  { label: 'Credit', value: 'credit' },
  { label: 'Debit', value: 'debit' },
]

// Payment statuses
const paymentStatuses = [
  { label: 'Pending', value: 'pending' },
  { label: 'Completed', value: 'completed' },
  { label: 'Cancelled', value: 'cancelled' },
]

// Watch for changes in local state and emit updates
watch(localTransactionDate, (value) => {
  emit('update:transactionDate', value)
})

watch(localTransactionType, (value) => {
  emit('update:transactionType', value)
})

watch(localPaymentMethod, (value) => {
  emit('update:paymentMethod', value)
})

watch(localPaymentStatus, (value) => {
  emit('update:paymentStatus', value)
})

watch(localNotes, (value) => {
  emit('update:notes', value)
})

// Watch for props changes to update local state
watch(
  () => props.transactionDate,
  (value) => {
    localTransactionDate.value = value
  },
)

watch(
  () => props.transactionType,
  (value) => {
    localTransactionType.value = value
  },
)

watch(
  () => props.paymentMethod,
  (value) => {
    localPaymentMethod.value = value
  },
)

watch(
  () => props.paymentStatus,
  (value) => {
    localPaymentStatus.value = value
  },
)

watch(
  () => props.notes,
  (value) => {
    localNotes.value = value
  },
)
</script>
